import { useState, useEffect, CSSProperties, MouseEventHandler } from "react";
import DialogComponent from "./DialogComponent";
import { NewsType } from "../types/main";
import PlayButton from "./ui-icons/buttons/PlayButton";
import Slider from "react-slick";
import {
  NextIcon,
  NextMobileIcon,
  PrevIcon,
  PrevMobileIcon,
} from "./ui-icons/slider";

import Loading from "./Loading";
import Text from "./ui/news/text";
import Video from "./ui/news/video";
import LinksList from "./ui/news/linksList";
import ImagesCollection from "./ui/news/imagesCollection";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import remarkBreaks from "remark-breaks";

import { twMerge } from "tailwind-merge";
import { getItemsRequest } from "src/modules/spItems/api";
import { NewsListName, strapiAPIUrl } from "src/api/config";
import moment from "moment";
import { truncateByWords } from "src/constants";

function PrevButton(props: {
  className: string;
  style?: CSSProperties;
  onClick?: MouseEventHandler;
}) {
  const { className, style, onClick } = props;
  return (
    <>
      <div
        className={twMerge(
          className,
          "flex place-items-center slick-prev before:hidden "
        )}
        onClick={onClick}
        style={{ ...style }}
      >
        <span className="max-sm:hidden">
          <PrevIcon />
        </span>
        <span className="hidden max-sm:block">
          <PrevMobileIcon />
        </span>
      </div>
    </>
  );
}

function NextButton(props: {
  className: string;
  style?: CSSProperties;
  onClick?: MouseEventHandler;
}) {
  const { className, style, onClick } = props;
  return (
    <>
      <div
        className={twMerge(
          className,
          "flex place-items-center slick-next before:hidden "
        )}
        onClick={onClick}
        style={{ ...style }}
      >
        <span className="max-sm:hidden">
          <NextIcon />
        </span>
        <span className="hidden max-sm:block">
          <NextMobileIcon />
        </span>
      </div>
    </>
  );
}

function NewsSide() {
  const [news, setNews] = useState<any>([]);
  const [customLoading, setCustomLoading] = useState(true);
  const [activeNews, setActiveNews] = useState<any>(null);
  const [open, setOpen] = useState(false);

  function readMore() {
    setOpen(true);
  }

  function updateNewsById(data: NewsType) {
    setActiveNews(data);
  }

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    fade: true,
    arrows: false,
    nextArrow: <NextButton className="-right-7" />,
    prevArrow: <PrevButton className="-left-7" />,
    responsive: [{ breakpoint: 1280, settings: { dots: false, arrows: true } }],
  };

  const dialogToggle = (b: boolean) => {
    setOpen(b);
  };

  useEffect(() => {
    getItemsRequest({
      listName: NewsListName,
      rowLimit: 3,
      sortOrder: { name: "ID", desc: true },
    }).then((results) => {
      setNews([...results.items]);
      setCustomLoading(false);
    });
  }, []);

  useEffect(() => {
    if (news.length > 0) {
      console.log("news: ", news);
      setActiveNews(news[0]);
    }
  }, [news]);

  if (customLoading) return <Loading />;
  if (activeNews === null)
    return (
      <div className="flex flex-row grow">
        <h3 className="flex flex-col w-full">Новостей нет</h3>
      </div>
    );

  return (
    <div className="hidden tablet:flex flex-row grow">
      <div className="flex flex-col w-full">
        <Slider
          rows={1}
          adaptiveHeight
          className="flex flex-col"
          beforeChange={(current, next) => updateNewsById(news[next])}
          dotsClass="slick-dots flex flex-row justify-between grow gap-7.5 mt-5"
          customPaging={(i: number) => (
            <div
              className={twMerge(
                activeNews.ID === news[i].ID ? "triangle-top" : "",
                "dot flex flex-col grow p-4 shadow-custom bg-white flex-1 md:max-h-[calc(100vh-81px)] rounded-lg cursor-pointer"
              )}
            >
              <div className="flex flex-row items-center justify-center mb-5">
                <div className="overflow-hidden rounded-lg aspect-4/3">
                  <img
                    src={news[i].FileRef || news[i].PostImage.get_url()}
                    alt="short-news"
                    className="object-contain w-full h-full"
                  />
                </div>
              </div>
              <div className="flex flex-row font-medium h-14">
                <p className="text-left line-clamp-3">{news[i].Title}</p>
              </div>
            </div>
          )}
          {...settings}
        >
          {news.map((news) => (
            <div key={news.ID} className="flex flex-row p-5 grow">
              <div className="flex flex-col">
                <div className="flex flex-row mb-4">
                  <h3>{news.Title}</h3>
                </div>
                <div className="flex flex-row gap-x-3 max-sm:flex-wrap grow">
                  <div className="flex flex-col h-full md:w-1/2 max-sm:mb-4">
                    <div className="flex flex-row items-center justify-center h-full overflow-hidden rounded-lg aspect-4/3">
                      <img
                        src={news.FileRef || news.PostImage.get_url()}
                        alt="preview-news"
                        className="object-contain w-full h-full"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col justify-between md:w-1/2">
                    <div className="flex flex-row mb-4">
                      <div className="flex flex-col">
                        <div className="flex flex-row mb-4">
                          <p>
                            <ReactMarkdown
                              skipHtml
                              remarkPlugins={[remarkBreaks, remarkGfm]}
                              rehypePlugins={[rehypeSanitize]}
                            >
                              {(news.Description &&
                                truncateByWords(
                                  news.Description,
                                  250
                                )) ||
                                ""}
                            </ReactMarkdown>
                          </p>
                        </div>
                        {news.Created && (
                          <div className="flex flex-row">
                            <div className="date">
                              {moment(news.Created).format("DD.MM.YYYY")}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-row items-start">
                      <button className="link" onClick={readMore}>
                        Прочитать полностью
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </div>
      {open && (
        <NewsDialog
          content={activeNews}
          open={open}
          toggleDialog={dialogToggle}
        />
      )}
    </div>
  );
}

export default NewsSide;

function NewsDialog({
  content,
  open,
  toggleDialog,
}: {
  content: any;
  open: boolean;
  toggleDialog: (b: boolean) => void;
}) {
  const [video, setVideo] = useState({
    view: false,
    exist: false,
    code: "",
    caption: "",
  });

  return (
    <DialogComponent type="news" open={open} toggleDialog={toggleDialog}>
      <div
        className={`flex flex-col h-full relative bg-white shadow-xl constructor`}
      >
        <div className="flex flex-row justify-between">
          <div className="flex flex-col">
            <h1 className="h1">{content.Title}</h1>
          </div>
        </div>
        <div className="flex flex-row">
          <div className="flex flex-col grow">
            {content.Created && (
              <div className="flex flex-row -mx-2 divide-x max-sm:flex-wrap max-sm:divide-none date">
                <div className="flex flex-col px-2 max-sm:w-full">
                  {moment(content.Created).format("DD.MM.YYYY")}
                </div>
              </div>
            )}
            <div className="video flex relative flex-row items-center justify-center sm:min-h-[290px] md:min-h-[400px] rounded-lg bg-white">
              <div
                style={{
                  backgroundImage: `url("${content.FileRef || content.PostImage.get_url()}")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center center",
                  backgroundSize: "contain",
                }}
                onClick={() => {
                  if (video.exist) {
                    setVideo({ ...video, view: true });
                  } else {
                    setVideo({ ...video, view: false });
                  }
                }}
                className={`cursor-pointer absolute w-full h-full top-0 bottom-0 left-0 right-0 z-50 flex flex-row items-center justify-center bg-white ${!video.view ? "" : "hidden"
                  }`}
              >
                {video.exist && <PlayButton />}
              </div>
              <div className="relative w-full h-full">
                <div
                  className="video-container"
                  dangerouslySetInnerHTML={{ __html: video.code }}
                ></div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-row flex-wrap" key={content.ID}>
          <div className="flex flex-row flex-wrap w-full">
            <div className="w-full section-block">
              {content.Description && <Text item={content} />}
              {content.VideoFrame && (
                <Video setVideo={setVideo} video={video} item={content} />
              )}
              {content.LinksList && content.LinksList.length > 0 && (
                <LinksList links={content.LinksList} />
              )}
              {content.FilesGallery && content.FilesGallery.length > 0 && (
                <ImagesCollection gallery={content.FilesGallery} />
              )}
            </div>
          </div>
        </div>
      </div>
    </DialogComponent>
  );
}
